import type {
  CreateIngredientPayload,
  CreateLotPayload,
  UpsertRecipePayload,
} from "@/modules/inventory/domain/types";
import type {
  InventoryLotsListParams,
  InventoryLotsSummaryParams,
} from "./client";
import type {
  InventoryMovementsListParams,
  InventoryMovementsSummaryParams,
} from "./client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inventoryIngredientsApi,
  inventoryLotsApi,
  inventoryMovementsApi,
  inventoryRecipesApi,
  stockTransfersApi,
  type StockTransfersListParams,
} from "./client";

export const inventoryIngredientsKeys = {
  all: ["inventory", "ingredients"] as const,
  detail: (id: string) => ["inventory", "ingredients", "detail", id] as const,
};

export function useIngredientsQuery() {
  return useQuery({
    queryKey: inventoryIngredientsKeys.all,
    queryFn: () => inventoryIngredientsApi.list(),
  });
}

export function useIngredientQuery(id: string) {
  return useQuery({
    queryKey: inventoryIngredientsKeys.detail(id),
    queryFn: () => inventoryIngredientsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateIngredient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIngredientPayload) =>
      inventoryIngredientsApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryIngredientsKeys.all });
    },
  });
}

export function useUpdateIngredient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateIngredientPayload) =>
      inventoryIngredientsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryIngredientsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: inventoryIngredientsKeys.all });
    },
  });
}

export function useBulkAssignIngredientCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      ingredientIds: string[];
      categoryId: string | null;
    }) => inventoryIngredientsApi.bulkAssignCategory(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryIngredientsKeys.all });
    },
  });
}

export function useDeleteIngredient() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => inventoryIngredientsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryIngredientsKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export const inventoryLotsKeys = {
  list: (params: InventoryLotsListParams) =>
    ["inventory", "lots", "list", params] as const,
  summary: (params: InventoryLotsSummaryParams) =>
    ["inventory", "lots", "summary", params] as const,
};

export function useLotsQuery(params: InventoryLotsListParams = {}) {
  return useQuery({
    queryKey: inventoryLotsKeys.list(params),
    queryFn: () => inventoryLotsApi.list(params),
  });
}

export function useLotsSummaryQuery(params: InventoryLotsSummaryParams = {}) {
  return useQuery({
    queryKey: inventoryLotsKeys.summary(params),
    queryFn: () => inventoryLotsApi.summary(params),
  });
}

export function useCreateLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLotPayload) =>
      inventoryLotsApi.createLot(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
    },
  });
}

export const inventoryMovementsKeys = {
  list: (params: InventoryMovementsListParams) =>
    ["inventory", "movements", "list", params] as const,
  summary: (params: InventoryMovementsSummaryParams) =>
    ["inventory", "movements", "summary", params] as const,
};

export function useMovementsQuery(params: InventoryMovementsListParams = {}) {
  return useQuery({
    queryKey: inventoryMovementsKeys.list(params),
    queryFn: () => inventoryMovementsApi.list(params),
  });
}

export function useMovementsSummaryQuery(
  params: InventoryMovementsSummaryParams = {},
) {
  return useQuery({
    queryKey: inventoryMovementsKeys.summary(params),
    queryFn: () => inventoryMovementsApi.summary(params),
  });
}

export function useCreateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { lotId: string; qtyDelta: number }) =>
      inventoryMovementsApi.createAdjustment(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory", "movements"] });
      qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
    },
  });
}

export const inventoryRecipesKeys = {
  byVariant: (variantId: string) =>
    ["inventory", "recipes", "variant", variantId] as const,
};

export function useRecipeByVariantId(variantId: string) {
  return useQuery({
    queryKey: inventoryRecipesKeys.byVariant(variantId),
    queryFn: () => inventoryRecipesApi.getByVariantId(variantId),
    enabled: !!variantId,
  });
}

export function useUpsertRecipe(variantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertRecipePayload) =>
      inventoryRecipesApi.upsert(variantId, payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: inventoryRecipesKeys.byVariant(variantId),
      });
    },
  });
}

export const stockTransfersKeys = {
  all: ["inventory", "transfers"] as const,
  list: (params: StockTransfersListParams) =>
    ["inventory", "transfers", "list", params] as const,
  detail: (id: string) => ["inventory", "transfers", "detail", id] as const,
};

export function useStockTransfersQuery(params: StockTransfersListParams) {
  return useQuery({
    queryKey: stockTransfersKeys.list(params),
    queryFn: () => stockTransfersApi.list(params),
  });
}

export function useStockTransferQuery(id: string) {
  return useQuery({
    queryKey: stockTransfersKeys.detail(id),
    queryFn: () => stockTransfersApi.get(id),
    enabled: !!id,
  });
}

function invalidateTransfers(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: stockTransfersKeys.all });
  if (id) {
    qc.invalidateQueries({ queryKey: stockTransfersKeys.detail(id) });
  }
}

export function useApproveStockTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof stockTransfersApi.approve>[1]) =>
      stockTransfersApi.approve(id, body),
    onSuccess: () => invalidateTransfers(qc, id),
  });
}

export function useRejectStockTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof stockTransfersApi.reject>[1]) =>
      stockTransfersApi.reject(id, body),
    onSuccess: () => invalidateTransfers(qc, id),
  });
}

export function useReviseApprovedStockTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof stockTransfersApi.reviseApproved>[1]) =>
      stockTransfersApi.reviseApproved(id, body),
    onSuccess: (item) => {
      qc.setQueryData(stockTransfersKeys.detail(id), item);
      invalidateTransfers(qc, id);
    },
  });
}

export function useCancelStockTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => stockTransfersApi.cancel(id),
    onSuccess: () => invalidateTransfers(qc, id),
  });
}

export function useShipStockTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof stockTransfersApi.ship>[1]) =>
      stockTransfersApi.ship(id, body),
    onSuccess: () => invalidateTransfers(qc, id),
  });
}

export function useCloseStockTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => stockTransfersApi.close(id),
    onSuccess: () => invalidateTransfers(qc, id),
  });
}
